import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import SEO from '../components/SEO';
import RelatedDestinations from '../components/RelatedDestinations';
import { useApp } from '../context/AppContext';
import { linkifyBlogBody } from '../lib/linkifyBlogBody';
import DOMPurify from 'dompurify';
import { Clock, Tag, ArrowLeft, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { allCountries } = useApp();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog/${slug}`);
        if (!cancelled && data?.success) setPost(data.post);
      } catch (e) {
        if (!cancelled) setNotFound(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-link country/region names in the post body to their /destinations/{id}
  // page. Only the FIRST occurrence per country is linked; headings and
  // existing anchors are skipped. This is the strongest internal-linking
  // SEO signal we can create between blog and commercial pages.
  // Output is then run through DOMPurify to strip any <script>/on*= vectors
  // — SEC-004 remediation, defense-in-depth on top of the admin-only blog
  // write route.
  // MUST be declared BEFORE the early returns below (React hooks order rule).
  const linkedBody = useMemo(
    () => DOMPurify.sanitize(
      linkifyBlogBody(post?.body || '', allCountries || []),
      { USE_PROFILES: { html: true } }
    ),
    [post, allCountries]
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-neutral-500" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-white dark:bg-neutral-950">
        <SEO noindex canonical={`/blog/${slug}`} />
        <Navbar />
        <div className="pt-32 text-center px-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-neutral-100 mb-4">Post not found</h1>
          <button onClick={() => navigate('/blog')} className="text-blue-600 dark:text-blue-400 hover:underline">
            Back to blog
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  // Article JSON-LD (Google rich results eligible)
  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.meta_description,
    image: post.header_image ? [post.header_image] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at,
    author: { '@type': 'Organization', name: post.author || 'Connectify' },
    publisher: {
      '@type': 'Organization',
      name: 'Connectify eSIMs',
      logo: {
        '@type': 'ImageObject',
        url: 'https://customer-assets.emergentagent.com/job_esim-connect-6/artifacts/3a8g7p2b_20571.webp',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://connectify-esims.com/blog/${post.slug}`,
    },
    keywords: (post.tags || []).join(', '),
  };

  return (
    <div className="min-h-screen bg-white dark:bg-neutral-950">
      <SEO
        title={post.title}
        description={post.meta_description}
        keywords={(post.tags || []).join(', ')}
        canonical={`/blog/${post.slug}`}
        image={post.header_image}
        type="article"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Blog', url: '/blog' },
          { name: post.title, url: `/blog/${post.slug}` },
        ]}
        structuredData={[articleLd]}
      />
      <Navbar />

      <article className="pt-28 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-gray-500 dark:text-neutral-400 hover:text-gray-900 dark:hover:text-neutral-100 mb-6"
            data-testid="back-to-blog"
          >
            <ArrowLeft size={16} /> Back to blog
          </Link>

          <header className="mb-8">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 dark:text-neutral-100 mb-4 leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-neutral-500">
              <span className="flex items-center gap-1">
                <Clock size={14} /> {post.reading_time_minutes || 1} min read
              </span>
              {post.published_at && (
                <time dateTime={post.published_at}>
                  {new Date(post.published_at).toLocaleDateString('en-AU', {
                    year: 'numeric', month: 'long', day: 'numeric',
                  })}
                </time>
              )}
              {post.author && <span>by {post.author}</span>}
            </div>
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {post.tags.map((t) => (
                  <span
                    key={t}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-gray-100 dark:bg-neutral-800 text-gray-700 dark:text-neutral-300"
                  >
                    <Tag size={10} /> {t}
                  </span>
                ))}
              </div>
            )}
          </header>

          {post.header_image && (
            <img
              src={post.header_image}
              alt={post.header_image_alt || post.title}
              className="w-full rounded-2xl mb-8 aspect-[16/9] object-cover"
            />
          )}

          <div
            data-testid="blog-post-body"
            className="blog-body text-gray-800 dark:text-neutral-200 leading-relaxed
              [&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-black [&_h2]:mt-10 [&_h2]:mb-4 [&_h2]:text-gray-900 [&_h2]:dark:text-neutral-100
              [&_h3]:text-xl [&_h3]:sm:text-2xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:text-gray-900 [&_h3]:dark:text-neutral-100
              [&_p]:mb-4 [&_p]:text-base [&_p]:sm:text-lg
              [&_a]:text-blue-600 [&_a]:dark:text-blue-400 [&_a]:underline [&_a]:decoration-blue-600/30 hover:[&_a]:decoration-blue-600
              [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul]:space-y-1
              [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol]:space-y-1
              [&_li]:leading-relaxed
              [&_strong]:font-bold [&_strong]:text-gray-900 [&_strong]:dark:text-neutral-100
              [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:dark:border-neutral-700 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-6
              [&_img]:rounded-xl [&_img]:my-6 [&_img]:max-w-full [&_img]:h-auto
              [&_code]:bg-gray-100 [&_code]:dark:bg-neutral-800 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm"
            dangerouslySetInnerHTML={{ __html: linkedBody }}
          />

          <RelatedDestinations tags={post.tags || []} countries={allCountries || []} />
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogPost;
